import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo } from 'react';

import TimeUtils from '@business/utils/time';
import { ThemeColors } from '@theme/foundations/colors';
import { Day } from '@calendar';
import { Tooltip } from '@chakra-ui/react';
import RestrictionsTooltip from './RestrictionsTooltip';
import { observer } from 'mobx-react';

type MonthProps = {
  date: Date;
  checkin?: Date;
  checkout?: Date;
  days?: Day[];
  onClickDate?: (date: Date) => void;
  onMouseEnterDay?: (event, day?: Day) => void;
  onMouseLeaveDay?: (event, day?: Day) => void;
};

const today: Date = TimeUtils.getToday();

function getDays(date: Date): number[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay: Date = new Date(year, month, 1);
  const lastDay: Date = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDay.getDay();
  const totalDays = lastDay.getDate();
  const days: number[] = [];
  let count = 1;
  for (let i = 0; i < 42; i++) {
    if (i < firstDayOfWeek || count > totalDays) {
      days.push(0);
      continue;
    }
    days.push(count);
    count++;
  }
  return days;
}

function getMonthName(date: Date, locale = 'default'): string {
  return date.toLocaleString(locale, { month: 'long' });
}

function getWeekDays(locale = 'default'): string[] {
  const date: Date = new Date(Date.UTC(2017, 0, 2)); // just a Monday
  const weekDays: string[] = [];
  for (let i = 0; i < 7; i++) {
    weekDays.push(date.toLocaleDateString(locale, { weekday: 'short' }));
    date.setDate(date.getDate() + 1);
  }
  return weekDays;
}

const DayBox = ({
  checkin,
  checkout,
  date,
  days,
  day,
  onClickDate,
}: {
  day: number;
} & MonthProps) => {
  const _date = useMemo(
    () => new Date(date.getFullYear(), date.getMonth(), day),
    [day, date]
  );
  const _dateString = useMemo(() => TimeUtils.format(_date), [_date]);

  const [showTooltip, setShowTooltip] = React.useState(false);

  const calendarDay = useMemo(() => {
    return days?.find((el) => el.date == _dateString);
  }, [_dateString, days]);

  const className = useMemo(() => {
    const classList: string[] = ['day'];
    let isCheckin = false;
    if (TimeUtils.isBefore(_date, today)) {
      classList.push('before');
    } else if (checkin && TimeUtils.isEqual(_date, checkin)) {
      isCheckin = true;
      classList.push('checkin');
      if (!checkout) classList.push('checkin-only');
    } else if (checkout && TimeUtils.isEqual(_date, checkout)) {
      classList.push('checkout');
    } else if (
      checkin &&
      checkout &&
      TimeUtils.isBetween(_date, checkin, checkout)
    ) {
      classList.push('range');
      if (calendarDay && !calendarDay.available) {
        classList.push('range-block');
      }
    } else if (calendarDay && !isCheckin && !calendarDay.available) {
      classList.push('block');
    }

    return classList.join(' ');
  }, [_date, calendarDay, checkin, checkout]);

  const handleMouseEnter = useCallback((e) => {
    if (
      calendarDay &&
      calendarDay.available &&
      calendarDay.restrictions.length > 0
    ) {
      setShowTooltip(true);
    }
  }, []);

  function handleMouseLeave(e) {
    setShowTooltip(false);
  }

  function onClickDay(e) {
    if (!calendarDay) return;
    if (process.env.NODE_ENV === 'development' && e.ctrlKey) {
      console.log(calendarDay.date, calendarDay);
      return;
    }
    if (calendarDay.available) onClickDate?.(_date);
  }

  if (day <= 0) return <></>;

  return (
    <Tooltip
      label={<RestrictionsTooltip day={calendarDay} />}
      placement="top"
      isOpen={showTooltip}
      bgColor="white"
      color="black"
      hasArrow={true}
      shadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
    >
      <div
        className={className}
        onClick={(e) => onClickDay(e)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {calendarDay &&
          calendarDay.available &&
          !calendarDay.past &&
          calendarDay.restrictions.length > 0 && <div className="dot"></div>}
        {day}
      </div>
    </Tooltip>
  );
};

const Month: React.FC<MonthProps> = (props: MonthProps) => {
  const daysToRender: number[] = getDays(props.date);
  const monthName: string = getMonthName(props.date);
  const weekDays: string[] = getWeekDays();

  return (
    <StyledMonth>
      <div className="caption">{monthName}</div>
      <div className="calendar-row calendar-day-names">
        {weekDays.map((elValue, elIndex) => (
          <div key={'weekDay-' + elIndex}>{elValue}</div>
        ))}
      </div>

      <div className="calendar-row calendar-days">
        {daysToRender.map((elValue, elIndex) => {
          return (
            (elValue > 0 || elIndex < 7) && (
              <div key={'day' + elIndex}>
                <DayBox key={elIndex} day={elValue} {...props} />
              </div>
            )
          );
        })}
      </div>
    </StyledMonth>
  );
};

const StyledMonth = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;

    width: calc(48px * 7);

    * {
        user-select: none;
    }

    .caption {
        font-size: 16px;
        font-weight: 500;
        height: 40px;
        line-height: 40px;
    }

    .calendar-row {
        display: flex;
        width: 100%;

        div {
            width: calc(100% / 7);
        }
    }
    .calendar-day-names {
        padding: 1rem 0;
        div {
            color: #778295;
        }
    }
    .calendar-days {
        flex-wrap: wrap;
        div {
            aspect-ratio : 1 / 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
        }

        .day {
            position: relative;
            border-radius: 4px;
            width: 100%;
            height: 100%;
            cursor: pointer;
        }

        .day:hover {
            border: 1px solid #32C0A0;
        }

        .range {
            border-radius: 0;
            background-color: ${ThemeColors.primary[300]};
        }

        .range-block {
            cursor: unset;
            border-radius: 0;
            color: rgba(0, 0, 0, 0.25);
            background-color: ${ThemeColors.primary[300]};
        }

        .checkin {
            border-radius: 4px 0 0 4px;
        }

        .checkout {
            border-radius: 0 4px 4px 0;
        }

        .checkin, .checkout {
            background-color: ${ThemeColors.primary[500]};
        }

        .checkin-only {
            border-radius: 4px;
        }

        .before {
            pointer-events: none;
            color: #BBC1CA;
        }

        .block {
            background-color: #F8F9FA;
            cursor: unset;
            border-radius: 0;
            color: #DDE0E4;
        }

        .block:hover {
            border: unset;
        }

        .dot {
            position: absolute;
            top: calc(50% - 20px);
            left: calc(50% - 3px);
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: ${ThemeColors.primary[500]};
        }
    }

    table {
        table-layout: fixed;
        width: 300px;
    }

    td {
        text-align: center;
        width: 100%;
        aspect-ratio : 1 / 1;
    }
`;

export default observer(Month);
